import React, { useEffect, useState } from "react";
import { 
  Grid, 
  Paper, 
  Typography, 
  Button, 
  Box, 
  Tabs, 
  Tab, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  TextField,
  Divider,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import HeaderIcons from "../../Images/headericon.png";
import { useDispatch, useSelector } from "react-redux";
import { 
  GetAllRoles, 
  GetApiPermission, 
  CreateSubRoleGroup, 
  GetRoleGroups,
  GetRoleGroupPermissions,
  UpdateRoleGroupPermissions
} from "../../redux/Actions/userStatusService";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function SubRoleManagement() {
  const dispatch = useDispatch();
  const { allRoles, userPermissions, roleGroups, roleGroupPermissions, loading } = useSelector(
    (state) => state.userStatus
  );
  
  const [tabValue, setTabValue] = useState(0);
  const [selectedRole, setSelectedRole] = useState("");
  const [groupName, setGroupName] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  
  useEffect(() => {
    dispatch(GetAllRoles());
    dispatch(GetApiPermission("ADMIN"));
    dispatch(GetRoleGroups());
  }, [dispatch]);
  
  useEffect(() => {
    if (selectedGroup) {
      dispatch(GetRoleGroupPermissions(selectedGroup.id));
    }
  }, [dispatch, selectedGroup]);
  
  useEffect(() => {
    if (roleGroupPermissions && roleGroupPermissions.length > 0) {
      const permissionsObj = {};
      roleGroupPermissions.forEach(permission => {
        permissionsObj[permission.apiName] = true;
      });
      setSelectedPermissions(permissionsObj);
    } else {
      setSelectedPermissions({});
    }
  }, [roleGroupPermissions]);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSelectedGroup(null);
    setSelectedPermissions({});
  };
  
  const handlePermissionChange = (apiName) => {
    setSelectedPermissions((prevPermissions) => ({
      ...prevPermissions,
      [apiName]: !prevPermissions[apiName],
    }));
  };
  
  const handleCreateGroup = () => {
    if (!selectedRole || !groupName) return;
    
    const permissionsArray = Object.entries(selectedPermissions)
      .filter(([, isSelected]) => isSelected)
      .map(([apiName]) => apiName);
    
    const payload = {
      groupRole: selectedRole,
      groupName: groupName,
      apiNames: permissionsArray
    };
    
    dispatch(CreateSubRoleGroup(payload)).then(() => {
      setOpenCreateDialog(false);
      setGroupName("");
      setSelectedRole("");
      setSelectedPermissions({});
      dispatch(GetRoleGroups());
    });
  };
  
  const handleUpdatePermissions = () => {
    if (!selectedGroup) return;
    
    const permissionsArray = Object.entries(selectedPermissions)
      .filter(([, isSelected]) => isSelected)
      .map(([apiName]) => apiName);
    
    const payload = {
      apiNames: permissionsArray
    };
    
    dispatch(UpdateRoleGroupPermissions({
      groupId: selectedGroup.id,
      body: payload
    })).then(() => {
      dispatch(GetRoleGroups());
    });
  };
  
  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
  };
  
  const handleOpenCreateDialog = () => {
    setOpenCreateDialog(true);
    setSelectedPermissions({});
  };
  
  return (
    <>
      <Grid item xs={12} sx={{ mt: 10 }}>
        <Paper
          sx={{ p: 2, display: "flex", flexDirection: "column" }}
          elevation={1}
        >
          <div className="row rows" style={{ marginLeft: "2px" }}>
            <div className="col-1">
              <img src={HeaderIcons} alt="Header Icon" width="45" height="45" />
            </div>
            <div
              className="col-10"
              style={{ marginTop: "10px", marginLeft: "-3%" }}
            >
              <Typography style={{ fontSize: "20px", color: "#18275A" }}>
                Sub Role Management
              </Typography>
            </div>
            <div
              className="col-1"
              style={{ marginTop: "10px", marginLeft: "-3%" }}
            >
              <Button
                sx={{ width: "120px" }}
                variant="contained"
                onClick={handleOpenCreateDialog}
                startIcon={<AddIcon />}
              >
                Create
              </Button>
            </div>
          </div>
        </Paper>
      </Grid>
      
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Paper sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="sub role management tabs">
              <Tab label="Manage Role Groups" />
              <Tab label="Assign Permissions" />
            </Tabs>
          </Box>
          
          <TabPanel value={tabValue} index={0}>
            <Typography variant="h6" gutterBottom>
              Role Groups
            </Typography>
            <List>
              {roleGroups && roleGroups.map((group) => (
                <ListItem
                  key={group.id}
                  secondaryAction={
                    <>
                      <IconButton edge="end" aria-label="edit" onClick={() => handleGroupSelect(group)}>
                        <EditIcon />
                      </IconButton>
                    </>
                  }
                  sx={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: '4px', 
                    mb: 1,
                    backgroundColor: selectedGroup?.id === group.id ? '#f5f5f5' : 'transparent'
                  }}
                >
                  <ListItemText 
                    primary={group.groupName} 
                    secondary={`Main Role: ${group.groupRole}`} 
                  />
                </ListItem>
              ))}
              {(!roleGroups || roleGroups.length === 0) && (
                <Typography variant="body2" color="text.secondary">
                  No role groups found. Create a new role group to get started.
                </Typography>
              )}
            </List>
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            {!selectedGroup ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h6" color="text.secondary">
                  Please select a role group from the "Manage Role Groups" tab first
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Assign Permissions to: {selectedGroup.groupName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Main Role: {selectedGroup.groupRole}
                  </Typography>
                </Box>
                
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Available Permissions:
                    </Typography>
                    
                    {userPermissions && userPermissions.length > 0 ? (
                      <List>
                        {userPermissions.map((permission) => (
                          <ListItem key={permission.apiName} dense>
                            <Checkbox
                              edge="start"
                              checked={!!selectedPermissions[permission.apiName]}
                              onChange={() => handlePermissionChange(permission.apiName)}
                            />
                            <ListItemText primary={permission.apiName} />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No permissions available
                      </Typography>
                    )}
                  </Grid>
                  
                  <Grid item xs={12} sx={{ mt: 2, textAlign: 'right' }}>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      onClick={handleUpdatePermissions}
                      disabled={loading}
                    >
                      Save Permissions
                    </Button>
                  </Grid>
                </Grid>
              </>
            )}
          </TabPanel>
        </Paper>
      </Grid>
      
      {/* Create Role Group Dialog */}
      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="md">
        <DialogTitle>Create New Role Group</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Group Name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Main Role</InputLabel>
                <Select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  label="Main Role"
                >
                  {allRoles && allRoles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Select Permissions:
              </Typography>
              
              {userPermissions && userPermissions.length > 0 ? (
                <List sx={{ maxHeight: '300px', overflow: 'auto' }}>
                  {userPermissions.map((permission) => (
                    <ListItem key={permission.apiName} dense>
                      <Checkbox
                        edge="start"
                        checked={!!selectedPermissions[permission.apiName]}
                        onChange={() => handlePermissionChange(permission.apiName)}
                      />
                      <ListItemText primary={permission.apiName} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No permissions available
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateGroup} 
            variant="contained" 
            color="primary"
            disabled={!selectedRole || !groupName || loading}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SubRoleManagement; 